import { Check, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface StickySaveBarProps {
  dirty: boolean;
  isSubmitting: boolean;
  /** Disabled-without-spinner gate (e.g. !isValid || !isDirty || hasErrors). */
  disabled?: boolean;
  onSave: () => void;
  saveLabel?: string;
  savingLabel?: string;
  /** Status text shown left of the save button when not dirty / not saving. */
  savedLabel?: string;
  /** Status text shown left of the save button when dirty. */
  dirtyLabel?: string;
  /** Optional inline error message rendered directly beneath the bar. */
  error?: string | null;
  className?: string;
}

/**
 * Visual rationale — sticky save bar:
 * - Three discrete states (saved / dirty / saving) drive the status indicator
 *   colour, animation, and the save button's enabled state. The bar itself
 *   stays the same height so its position never jumps as state flips.
 * - Status indicator is a 6px dot — neutral when saved, primary-400 when
 *   dirty, animated pulse when saving. A subtle dot, not a chip, keeps the
 *   bar from competing with the page's content.
 * - Border accent shifts to primary-200 when dirty so the bar reads as
 *   "needs your attention" without resorting to a coloured background fill.
 * - Compact px-5 py-2.5 padding keeps the bar shallow; rounded-xl matches
 *   the surrounding card surfaces.
 */
export function StickySaveBar({
  dirty,
  isSubmitting,
  disabled,
  onSave,
  saveLabel = 'Save changes',
  savingLabel = 'Saving…',
  savedLabel = 'All changes saved',
  dirtyLabel = 'Unsaved changes',
  error,
  className,
}: StickySaveBarProps) {
  const submitDisabled = isSubmitting || (disabled ?? false);

  return (
    <div className={cn('sticky bottom-4 z-10 flex flex-col gap-2', className)}>
      <div
        className={cn(
          'bg-surface flex items-center justify-between gap-3 rounded-xl border px-5 py-2.5 shadow-sm transition-[border-color]',
          dirty || isSubmitting ? 'border-primary-200' : 'border-(--color-border)',
        )}
      >
        <span className="flex items-center gap-2.5" aria-live="polite">
          <StatusIndicator dirty={dirty} isSubmitting={isSubmitting} />
          <span
            className={cn(
              'text-body-sm',
              dirty || isSubmitting ? 'text-accent-ink font-medium' : 'text-neutral-500',
            )}
          >
            {isSubmitting ? savingLabel : dirty ? dirtyLabel : savedLabel}
          </span>
        </span>
        <Button
          type="button"
          size="md"
          onClick={onSave}
          disabled={submitDisabled}
          className="min-w-32"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              {savingLabel}
            </>
          ) : (
            saveLabel
          )}
        </Button>
      </div>
      {error ? (
        <p className="text-error-700 text-body-sm pl-1" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function StatusIndicator({ dirty, isSubmitting }: { dirty: boolean; isSubmitting: boolean }) {
  if (isSubmitting) {
    return (
      <span
        aria-hidden="true"
        className="bg-primary-400 inline-block h-1.5 w-1.5 animate-pulse rounded-full"
      />
    );
  }
  if (dirty) {
    return (
      <span
        aria-hidden="true"
        className="bg-primary-400 ring-primary-100 inline-block h-1.5 w-1.5 rounded-full ring-2"
      />
    );
  }
  // Saved — a check mark inside a soft mint disc reads cleaner than a grey
  // dot at this size and reinforces the "all good" signal.
  return (
    <span
      aria-hidden="true"
      className="bg-primary-50 text-primary-400 inline-flex h-4 w-4 items-center justify-center rounded-full"
    >
      <Check className="h-3 w-3" strokeWidth={2.25} />
    </span>
  );
}
